import { useState, useEffect, use } from 'react';
import {
    collection, query, orderBy, getDocs, doc, updateDoc,
    getFirestore, limit, where, Timestamp
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    MessageSquare, CheckCircle, Clock, Filter,
    Archive, Trash2, ExternalLink, User as UserIcon,
    AlertTriangle, Bug, ThumbsUp, MoreHorizontal, Brain, RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { Mail } from 'lucide-react';

interface FeedbackItem extends AIFeedbackData {
    id: string;
    status?: 'new' | 'archived';
}

export default function AdminFeedback() {
    const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'new' | 'archived'>('new');
    const [generatingStudies, setGeneratingStudies] = useState<Set<string>>(new Set());

    const { get: getPassage } = use(PassageDataContext);

    const fetchFeedback = async () => {
        setIsLoading(true);
        try {
            const db = getFirestore(getApp());
            const feedbackRef = collection(db, 'feedback');

            let q;
            if (filter === 'all') {
                q = query(feedbackRef, orderBy('timestamp', 'desc'), limit(100));
            } else {
                q = query(
                    feedbackRef,
                    where('status', '==', filter === 'new' ? null : 'archived'),
                    orderBy('timestamp', 'desc'),
                    limit(100)
                );
            }

            // Special case for 'new': Firestore doesn't support where status == null and order by timestamp easily if some docs have status and some don't
            // So we'll fetch more and filter in memory for simplicity or fetch all and filter.
            // Actually, let's just fetch recent 100 and filter in memory for 'new' vs 'archived'.
            const allRecentQuery = query(feedbackRef, orderBy('timestamp', 'desc'), limit(200));
            const snapshot = await getDocs(allRecentQuery);

            const items = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    timestamp: data.timestamp instanceof Timestamp ? data.timestamp.toDate() : new Date(data.timestamp)
                } as FeedbackItem;
            });

            if (filter === 'new') {
                setFeedback(items.filter(i => !i.status || i.status === 'new'));
            } else if (filter === 'archived') {
                setFeedback(items.filter(i => i.status === 'archived'));
            } else {
                setFeedback(items);
            }
        } catch (error) {
            console.error('Error fetching feedback:', error);
            toast.error('Failed to load feedback');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchFeedback();
    }, [filter]);

    const handleArchive = async (id: string, currentStatus?: string) => {
        try {
            const db = getFirestore(getApp());
            const docRef = doc(db, 'feedback', id);
            const newStatus = currentStatus === 'archived' ? 'new' : 'archived';

            await updateDoc(docRef, {
                status: newStatus,
                dateUpdated: new Date()
            });

            toast.success(newStatus === 'archived' ? 'Feedback archived' : 'Feedback restored');
            fetchFeedback();
        } catch (error) {
            console.error('Error updating feedback:', error);
            toast.error('Failed to update feedback');
        }
    };

    const getTypeBadge = (type: string) => {
        switch (type) {
            case 'content_inaccuracy':
                return <Badge variant="destructive" className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Inaccuracy</Badge>;
            case 'bug':
                return <Badge variant="destructive" className="flex items-center gap-1 bg-red-600"><Bug className="h-3 w-3" /> Bug</Badge>;
            case 'suggestion':
                return <Badge variant="secondary" className="flex items-center gap-1 bg-blue-100 text-blue-700 hover:bg-blue-100 border-none"><Clock className="h-3 w-3" /> Suggestion</Badge>;
            case 'praise':
                return <Badge variant="secondary" className="flex items-center gap-1 bg-green-100 text-green-700 hover:bg-green-100 border-none"><ThumbsUp className="h-3 w-3" /> Praise</Badge>;
            case 'study_request':
                return <Badge variant="secondary" className="flex items-center gap-1 bg-purple-100 text-purple-700 hover:bg-purple-100 border-none"><Brain className="h-3 w-3" /> Study Request</Badge>;
            default:
                return <Badge variant="outline">{type}</Badge>;
        }
    };

    const handleGenerateStudy = async (passageId: string, feedbackId: string) => {
        if (!confirm('Generate Guided Study with Gemini 3.1 Pro? This may take a few minutes.')) {
            return;
        }

        setGeneratingStudies(prev => new Set(prev).add(feedbackId));
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const generateFunction = httpsCallable(functions, 'generateGuidedStudyByPassage', {
            timeout: 300000 // 5 minutes
        });

        try {
            console.log(`Generating guided study for passage ${passageId}...`);
            await generateFunction({ passageId, model: 'gemini-3.1-pro-preview' });
            console.log(`Successfully generated guided study for passage ${passageId}`);
            toast.success('Guided study generated successfully!');
            // Optionally archive the feedback item
            await handleArchive(feedbackId, 'new');
        } catch (error) {
            console.error(`Failed to generate guided study for passage ${passageId}:`, error);
            toast.error('Failed to generate guided study. Check console for details.');
        } finally {
            setGeneratingStudies(prev => {
                const next = new Set(prev);
                next.delete(feedbackId);
                return next;
            });
        }
    };

    const renderContent = (content: string, passageId?: string) => {
        if (!passageId) return content;
        const passage = getPassage(passageId);
        if (!passage) return content;

        const reference = `${passage.book} ${passage.ref}`;
        // If the content specifically mentions the passage ID, replace it with the reference
        if (content.includes(passageId)) {
            return content.split(passageId).join(reference);
        }
        return content;
    };

    return (
        <Card className="w-full">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                    <CardTitle className="text-xl flex items-center gap-2">
                        <MessageSquare className="h-5 w-5" />
                        User Feedback
                    </CardTitle>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant={filter === 'new' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilter('new')}
                        className="h-8"
                    >
                        Active
                    </Button>
                    <Button
                        variant={filter === 'archived' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilter('archived')}
                        className="h-8"
                    >
                        Archived
                    </Button>
                    <Button
                        variant={filter === 'all' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilter('all')}
                        className="h-8"
                    >
                        All
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <Clock className="h-8 w-8 animate-spin text-muted-foreground opacity-20" />
                    </div>
                ) : feedback.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground italic">
                        No feedback found matching the current filter.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {feedback.map((item) => (
                            <div
                                key={item.id}
                                className={`p-4 rounded-lg border transition-all ${item.status === 'archived' ? 'opacity-60 bg-muted/30' : 'bg-card shadow-sm'
                                    }`}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {getTypeBadge(item.type)}
                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Clock className="h-3 w-3" />
                                            {format(item.timestamp as Date, 'MMM d, h:mm a')}
                                        </span>
                                        {item.contextType && (
                                            <Badge variant="outline" className="text-[10px] h-4 px-1 opacity-70">
                                                {item.contextType}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {item.type === 'study_request' && item.passageId && (
                                            <Button
                                                size="sm"
                                                variant="default"
                                                className="h-8 text-xs gap-1"
                                                onClick={() => handleGenerateStudy(item.passageId!, item.id)}
                                                disabled={generatingStudies.has(item.id)}
                                            >
                                                {generatingStudies.has(item.id) ? (
                                                    <>
                                                        <RefreshCw className="h-3 w-3 animate-spin" />
                                                        Generating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Brain className="h-3 w-3" />
                                                        Generate
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                            onClick={() => handleArchive(item.id, item.status)}
                                            title={item.status === 'archived' ? "Restore" : "Archive"}
                                        >
                                            {item.status === 'archived' ? <CheckCircle className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                                        </Button>
                                    </div>
                                </div>

                                <p className="text-sm my-3 whitespace-pre-wrap leading-relaxed">
                                    {renderContent(item.content, item.passageId)}
                                </p>

                                <div className="flex items-center justify-between mt-4 pt-3 border-t border-muted/50 text-[10px] text-muted-foreground font-mono">
                                    <div className="flex items-center gap-4">
                                        <span className="flex items-center gap-1">
                                            <UserIcon className="h-3 w-3" />
                                            {item.userId.substring(0, 8)}...
                                        </span>
                                        {item.email && (
                                            <span className="flex items-center gap-1">
                                                <Mail className="h-3 w-3" />
                                                {item.email}
                                            </span>
                                        )}
                                        {item.passageId && (() => {
                                            const passage = getPassage(item.passageId);
                                            return (
                                                <a
                                                    href={`/app/passage/${item.passageId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-1 hover:text-primary transition-colors underline decoration-dotted underline-offset-2"
                                                >
                                                    <ExternalLink className="h-3 w-3" />
                                                    {passage ? `${passage.book} ${passage.ref}` : item.passageId}
                                                </a>
                                            );
                                        })()}
                                    </div>
                                    <span className="truncate max-w-[200px]" title={item.userAgent}>
                                        {item.url}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
